import React from 'react';
import { 
  Typography, 
  TextField, 
  Box,
  Paper,
  Chip,
  Divider
} from '@mui/material';
import { AverageWorkSettings } from '../types';

interface AverageWorkCalculatorProps {
  settings: AverageWorkSettings;
  onSettingsChange: (settings: AverageWorkSettings) => void;
}

const AverageWorkCalculator: React.FC<AverageWorkCalculatorProps> = ({ 
  settings, 
  onSettingsChange
}) => {
  // 计算需要加班的天数
  const calculateOvertimeDays = () => {
    const { expectedAverageHours, overtimeWorkHours, normalWorkHours } = settings;
    
    // 使用公式：正常工作日上班时间*(22-x) + 加班日上班时间*x = 预期平均工时*22
    // 解方程：normalWorkHours * (22 - x) + overtimeWorkHours * x = expectedAverageHours * 22
    // 展开：normalWorkHours * 22 - normalWorkHours * x + overtimeWorkHours * x = expectedAverageHours * 22
    // 移项：overtimeWorkHours * x - normalWorkHours * x = expectedAverageHours * 22 - normalWorkHours * 22
    // 合并：x * (overtimeWorkHours - normalWorkHours) = 22 * (expectedAverageHours - normalWorkHours)
    // 解得：x = 22 * (expectedAverageHours - normalWorkHours) / (overtimeWorkHours - normalWorkHours)
    
    const numerator = 22 * (expectedAverageHours - normalWorkHours);
    const denominator = overtimeWorkHours - normalWorkHours;
    
    // 如果分母为0或负数，说明加班日工时不大于正常工作日工时
    if (denominator <= 0) {
      return 0;
    }
    
    // 如果分子为0或负数，说明正常工作日工时已经达到或超过预期
    if (numerator <= 0) {
      return 0;
    }
    
    const overtimeDays = numerator / denominator;
    
    // 如果计算结果超过22天，说明无法达到预期
    if (overtimeDays > 22) {
      return 22; // 返回22表示需要全部加班
    }
    
    return Math.ceil(overtimeDays);
  };

  // 计算实际平均工时
  const calculateActualAverageHours = () => {
    const { overtimeWorkHours, normalWorkHours } = settings;
    const overtimeDays = calculateOvertimeDays();
    const normalDays = 22 - overtimeDays;
    
    const totalHours = (normalDays * normalWorkHours) + (overtimeDays * overtimeWorkHours);
    return totalHours / 22;
  };

  const overtimeDays = calculateOvertimeDays();
  const actualAverageHours = calculateActualAverageHours();
  const normalDays = 22 - overtimeDays;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* 设置区域 */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="h6" color="primary">
          每月平均工时设置
        </Typography>
        <TextField
          fullWidth
          label="预期每月平均工时（小时）"
          type="number"
          inputProps={{ step: 0.1 }}
          value={settings.expectedAverageHours}
          onChange={(e) => onSettingsChange({ ...settings, expectedAverageHours: Number(e.target.value) })}
        />
        <TextField
          fullWidth
          label="加班日上班时间（小时）"
          type="number"
          inputProps={{ step: 0.1 }}
          value={settings.overtimeWorkHours}
          onChange={(e) => onSettingsChange({ ...settings, overtimeWorkHours: Number(e.target.value) })}
        />
        <TextField
          fullWidth
          label="正常工作日上班时间（小时）"
          type="number"
          inputProps={{ step: 0.1 }}
          value={settings.normalWorkHours}
          onChange={(e) => onSettingsChange({ ...settings, normalWorkHours: Number(e.target.value) })}
        />
      </Box>

      <Divider />

      {/* 计算结果 */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="h6" color="primary">
          计算结果
        </Typography>
        
        <Paper sx={{ p: 3, bgcolor: 'background.paper', borderRadius: '11px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body1">预期每月平均工时:</Typography>
              <Chip label={`${settings.expectedAverageHours} 小时`} color="primary" />
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body1">实际每月平均工时:</Typography>
              <Chip 
                label={`${actualAverageHours.toFixed(1)} 小时`} 
                color={Math.abs(actualAverageHours - settings.expectedAverageHours) < 0.1 ? 'success' : 'warning'} 
              />
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body1">需要加班天数:</Typography>
              <Chip 
                label={`${overtimeDays} 天`} 
                color={overtimeDays === 0 ? 'success' : 'warning'} 
              />
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body1">正常工作日:</Typography>
              <Chip label={`${normalDays} 天`} color="default" />
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body1">每月总工时:</Typography>
              <Chip label={`${(actualAverageHours * 22).toFixed(1)} 小时`} color="info" />
            </Box>
          </Box>
        </Paper>

        {/* 详细说明 */}
        {overtimeDays > 0 && overtimeDays < 22 && (
          <Paper sx={{ p: 2, bgcolor: 'warning.light', borderRadius: '11px' }}>
            <Typography variant="body2" color="warning.dark">
              💡 建议：每月需要加班 {overtimeDays} 天，每天工作 {settings.overtimeWorkHours} 小时，
              其余 {normalDays} 天正常工作 {settings.normalWorkHours} 小时，即可达到预期每月平均工时。
            </Typography>
          </Paper>
        )}

        {overtimeDays === 0 && (
          <Paper sx={{ p: 2, bgcolor: 'success.light', borderRadius: '11px' }}>
            <Typography variant="body2" color="success.dark">
              ✅ 恭喜！正常工作日工时已经达到预期每月平均工时，无需加班。
            </Typography>
          </Paper>
        )}

        {overtimeDays >= 22 && (
          <Paper sx={{ p: 2, bgcolor: 'error.light', borderRadius: '11px' }}>
            <Typography variant="body2" color="error.dark">
              ⚠️ 警告：即使全部22天都加班工作 {settings.overtimeWorkHours} 小时，
              也无法达到预期每月平均工时 {settings.expectedAverageHours} 小时。
              建议调整预期工时或增加加班日工时。
            </Typography>
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default AverageWorkCalculator;
